import React from "react";
import styled from "@emotion/styled";

import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  TextField as MuiTextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import useAppSelector from "../../../hooks/useAppSelector";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { RootState } from "../../../redux/store";
import {
  setConfigDialogState,
  setResultsToDisplay,
  setSearchType,
  deleteFilesFromSearch,
  addFilesToSearch,
} from "../../../redux/slices/search";
import { File } from "../../../types/app";

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-evenly;
  min-width: 350px;
  margin-top: 10px;
`;

const Button = styled(MuiButton)<{ selected: boolean }>`
  background-color: ${(props) =>
    props.selected
      ? props.theme.palette.primary
      : props.theme.palette.background.default};
  color: ${(props) =>
    props.selected
      ? props.theme.palette.background.default
      : props.theme.palette.primary.main};
  &:hover {
    background-color: ${(props) =>
      props.selected
        ? props.theme.palette.primary.main
        : props.theme.palette.secondary.main};
    color: ${(props) => props.theme.palette.background.default};
  }
`;

const TextField = styled(MuiTextField)`
  margin-top: 10px;

  .MuiInputBase-input {
    padding: 0;
    height: 35px;
    margin-left: 8px;
  }

  .MuiInputLabel-root {
    top: -8px;

    &.Mui-focused {
      top: 3px;
    }

    &.MuiInputLabel-shrink {
      top: 3px;
    }
  }
`;

const FileSearch = styled.div`
  max-height: 400px;
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CheckButton = styled(MuiButton)`
  height: 26px;
  margin-top: 4px;
  font-size: 12px;
  padding: 10px;
`;

function ConfigurationDialog() {
  const state = useAppSelector((state: RootState) => state.main);
  const searchState = useAppSelector((state: RootState) => state.search);
  const dispatch = useAppDispatch();
  const files = state.files;
  const filesToSearch = searchState.filesToSearch;

  function handleCheckboxClick(
    e: React.SyntheticEvent,
    fileName: string,
    fileUid: string
  ) {
    if (filesToSearch.hasOwnProperty(fileName)) {
      dispatch(deleteFilesFromSearch(fileName));
    } else {
      dispatch(addFilesToSearch({ fileId: fileUid, fileName: fileName }));
    }
  }

  function checkAll() {
    files.forEach((file: File) => {
      dispatch(
        addFilesToSearch({ fileId: file.fileUid, fileName: file.fileName })
      );
    });
  }

  function uncheckAll() {
    files.forEach((file: File) => {
      dispatch(deleteFilesFromSearch(file.fileName));
    });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    if (newValue === "" || parseInt(newValue) < 1) {
      dispatch(setResultsToDisplay(0));
    } else {
      // Remove leading zero
      const trimmedValue = newValue.replace(/^0+/, "");
      dispatch(setResultsToDisplay(parseInt(trimmedValue)));
    }
  }

  return (
    <Dialog
      open={searchState.configDialogOpen}
      onClose={() => dispatch(setConfigDialogState())}
    >
      <DialogTitle>Search Configurations</DialogTitle>
      <DialogContent sx={{ overflow: "hidden" }}>
        <Box display="flex">
          <Box>
            <Typography>Search Type: </Typography>
            <ButtonGroup>
              <Button
                variant="contained"
                selected={searchState.searchType === "speed"}
                onClick={() => dispatch(setSearchType("speed"))}
              >
                Speed
              </Button>
              <Button
                variant="contained"
                selected={searchState.searchType === "default"}
                onClick={() => dispatch(setSearchType("default"))}
              >
                Default
              </Button>
              <Button
                variant="contained"
                selected={searchState.searchType === "accuracy"}
                onClick={() => dispatch(setSearchType("accuracy"))}
              >
                Accuracy
              </Button>
            </ButtonGroup>
          </Box>
          <Box>
            <Typography>Results to return:</Typography>
            <TextField
              label="Number of Results"
              value={
                searchState.resultsToDisplay === 0
                  ? ""
                  : searchState.resultsToDisplay
              }
              type="number"
              onChange={handleChange}
            />
          </Box>
        </Box>
        <Typography sx={{ marginTop: "12px" }}>Files to Search: </Typography>
        <CheckButton variant="contained" onClick={() => checkAll()}>
          Check All
        </CheckButton>
        <CheckButton
          variant="contained"
          onClick={() => uncheckAll()}
          sx={{ marginLeft: "10px" }}
        >
          Uncheck All
        </CheckButton>
        <FileSearch>
          {files.map((file: File) => {
            return (
              <FormControlLabel
                key={file.fileName}
                control={<Checkbox />}
                label={
                  <Typography width="230px" noWrap>
                    {file.fileName}
                  </Typography>
                }
                checked={filesToSearch.hasOwnProperty(file.fileName)}
                onChange={(event) =>
                  handleCheckboxClick(event, file.fileName, file.fileUid)
                }
              />
            );
          })}
        </FileSearch>
      </DialogContent>
      <DialogActions>
        <MuiButton
          onClick={() => dispatch(setConfigDialogState())}
          color="primary"
        >
          Submit
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}

export default ConfigurationDialog;
